import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import UploadRemittanceModal from '../../components/Modals/Zappay/UploadRemittanceModal';
import SubmitUtrModal from '../../components/Modals/Zappay/SubmitUtrModal';
import { getRemittanceFiles } from '../../store/actions/authActionCreator';
import { getPermissions } from '../../utils/helper';

const STATUS_OPTIONS = ['GENERATED', 'UPLOADED', 'REMITTED'];

function SelectionScreen({ onSelect }) {
    return (
        <Box style={{ display: 'flex', gap: '24px', padding: '24px' }}>
            <Paper
                elevation={2}
                onClick={() => onSelect('legacy')}
                style={{
                    flex: 1,
                    padding: '32px 24px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    border: '2px solid transparent',
                    transition: 'border 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.border = '2px solid #43419e';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(67,65,158,0.15)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.border = '2px solid transparent';
                    e.currentTarget.style.boxShadow = '';
                }}
            >
                <FileCopyIcon style={{ fontSize: '48px', color: '#43419e' }} />
                <Typography variant="h6" style={{ fontWeight: 600, color: '#14112d' }}>
                    Legacy Remittance
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                    Upload your remittance file manually. Accepted format is <b>.csv</b>
                </Typography>
            </Paper>

            <Paper
                elevation={2}
                onClick={() => onSelect('new')}
                style={{
                    flex: 1,
                    padding: '32px 24px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    border: '2px solid transparent',
                    transition: 'border 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.border = '2px solid #43419e';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(67,65,158,0.15)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.border = '2px solid transparent';
                    e.currentTarget.style.boxShadow = '';
                }}
            >
                <FileCopyIcon style={{ fontSize: '48px', color: '#43419e' }} />
                <Typography variant="h6" style={{ fontWeight: 600, color: '#14112d' }}>
                    New Remittance
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                    Select a remittance file and submit UTR details
                </Typography>
            </Paper>
        </Box>
    );
}

function LegacyView({ onBack, permissions }) {
    const [remittanceModalOpen, setRemittanceModalOpen] = useState(false);

    return (
        <Box style={{ padding: '16px' }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                style={{ marginBottom: '16px' }}
            >
                Back
            </Button>
            <Paper elevation={1}>
                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                    }}
                >
                    <Box style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <FileCopyIcon style={{ fontSize: '32px', color: '#43419e' }} />
                        <Box style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                Remittance Upload
                            </Typography>
                            <Typography variant="body1" color="textSecondary">
                                Upload your remittance file here. Accepted format is <b>.csv</b>
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        className="btn-pos"
                        onClick={() => setRemittanceModalOpen(true)}
                        style={{
                            height: '100%',
                            padding: '12px 20px',
                            fontWeight: 600,
                            fontSize: '16px',
                        }}
                        disabled={!permissions.create}
                    >
                        Upload Remittance
                    </Button>
                </Box>
            </Paper>

            {remittanceModalOpen && (
                <UploadRemittanceModal
                    source="zappay"
                    open={remittanceModalOpen}
                    onClose={() => setRemittanceModalOpen(false)}
                />
            )}
        </Box>
    );
}

function NewRemittanceView({ onBack, permissions }) {
    const [selectedStatus, setSelectedStatus] = useState('GENERATED');
    const [remittanceFiles, setRemittanceFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [selectedRemittanceId, setSelectedRemittanceId] = useState(null);
    const [submitUtrModalOpen, setSubmitUtrModalOpen] = useState(false);

    const dispatch = useDispatch();

    const fetchFiles = (status) => {
        setFilesLoading(true);
        setSelectedRemittanceId(null);
        dispatch(
            getRemittanceFiles(
                status,
                (response) => {
                    setRemittanceFiles(Array.isArray(response) ? response : []);
                    setFilesLoading(false);
                },
                () => {
                    setRemittanceFiles([]);
                    setFilesLoading(false);
                }
            )
        );
    };

    useEffect(() => {
        fetchFiles(selectedStatus);
    }, [selectedStatus]);

    return (
        <Box style={{ padding: '16px' }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                style={{ marginBottom: '16px' }}
            >
                Back
            </Button>
            <Paper elevation={1}>
                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                    }}
                >
                    <Box style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <FileCopyIcon style={{ fontSize: '32px', color: '#43419e' }} />
                        <Box style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                New Remittance
                            </Typography>
                            <Typography variant="body1" color="textSecondary">
                                Select a generated file and submit UTR
                            </Typography>
                        </Box>
                    </Box>

                    <Box style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '220px' }}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                label="Status"
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {s}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Remittance File</InputLabel>
                            <Select
                                value={selectedRemittanceId ?? ''}
                                onChange={(e) => setSelectedRemittanceId(e.target.value)}
                                label="Remittance File"
                                disabled={filesLoading}
                            >
                                {filesLoading ? (
                                    <MenuItem disabled>
                                        <CircularProgress size={16} style={{ marginRight: 8 }} />
                                        Loading...
                                    </MenuItem>
                                ) : remittanceFiles.length === 0 ? (
                                    <MenuItem disabled>No files found</MenuItem>
                                ) : (
                                    remittanceFiles.map((file) => (
                                        <MenuItem key={file.id} value={file.remittanceId}>
                                            {file.fileName}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            color="primary"
                            className="btn-pos"
                            onClick={() => setSubmitUtrModalOpen(true)}
                            style={{
                                padding: '10px 20px',
                                fontWeight: 600,
                                fontSize: '14px',
                            }}
                            disabled={!permissions.create || !selectedRemittanceId || selectedStatus === 'REMITTED'}
                        >
                            Submit UTR
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {submitUtrModalOpen && (
                <SubmitUtrModal
                    open={submitUtrModalOpen}
                    onClose={() => setSubmitUtrModalOpen(false)}
                    remittanceId={selectedRemittanceId}
                    onSuccess={() => fetchFiles(selectedStatus)}
                />
            )}
        </Box>
    );
}

function Remittance() {
    const [view, setView] = useState(null); // null = selection, 'legacy', 'new'
    const permissions = useMemo(() => getPermissions('Remittance'), []);

    if (view === 'legacy') return <LegacyView onBack={() => setView(null)} permissions={permissions} />;
    if (view === 'new') return <NewRemittanceView onBack={() => setView(null)} permissions={permissions} />;
    return <SelectionScreen onSelect={setView} />;
}

export default Remittance;
